import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import type { PlaceNode } from '@ulysses-inc/harami_api_client'
import type { FC } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import PlaceGroupsTree from 'src/features/schedules/editSchedule/PlaceGroupsTree'
import { FieldBlock } from './FieldBlock'
import type { TaskScheduleForm } from './taskScheduleForm'

type TemplateOption = { id: number; name: string }

type Props = {
  templates: TemplateOption[]
  placeNodes: PlaceNode[]
  errorsOf: (field: string) => string[]
}

/**
 * 件名・ひな形・対象現場。ひな形と現場ツリーは旧スケジュールの編集から流用する。
 */
export const BasicsSection: FC<Props> = ({
  templates,
  placeNodes,
  errorsOf,
}) => {
  const { control, setValue } = useFormContext<TaskScheduleForm>()
  const placeNodeUuids = useWatch({ control, name: 'placeNodeUuids' })

  return (
    <Stack spacing={2}>
      <FieldBlock label="件名" required errors={errorsOf('name')}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <TextField
              {...field}
              id="task-schedule-name"
              size="small"
              fullWidth
              slotProps={{ htmlInput: { maxLength: 255 } }}
              placeholder="例: 冷蔵設備の温度確認"
            />
          )}
        />
      </FieldBlock>
      <FieldBlock label="ひな形" required errors={errorsOf('templateId')}>
        <Controller
          control={control}
          name="templateId"
          render={({ field }) => (
            <Autocomplete<TemplateOption, false, false, false>
              id="task-schedule-template"
              fullWidth
              options={templates}
              value={templates.find(t => t.id === field.value) ?? null}
              onChange={(_, option) => field.onChange(option?.id ?? null)}
              getOptionKey={option => String(option.id)}
              getOptionLabel={option => option.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="ひな形を選択"
                />
              )}
            />
          )}
        />
      </FieldBlock>
      <FieldBlock
        label="対象現場"
        required
        note="現場グループを選ぶと、配下の現場をまとめて選べます"
        errors={errorsOf('placeNodeUuids')}
      >
        {placeNodes.length > 0 ? (
          <PlaceGroupsTree
            placeNodes={placeNodes}
            checkedKeys={placeNodeUuids}
            onCheck={checked => {
              const uuids = Array.isArray(checked) ? checked : checked.checked
              setValue('placeNodeUuids', uuids.map(String), {
                shouldDirty: true,
              })
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            現場が登録されていないため、作業指示を作成できません。
          </Typography>
        )}
      </FieldBlock>
    </Stack>
  )
}
